import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Building2, Car, ShieldCheck, Wrench, ShoppingCart, Smile, UtensilsCrossed, Home, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";
const API = `${process.env.REACT_APP_API_URL}/api`;

const INDUSTRY_ICONS = {
  hvac: Wrench,
  insurance: ShieldCheck,
  auto_repair: Car,
  grocery: ShoppingCart,
  dental: Smile,
  restaurant: UtensilsCrossed,
  real_estate: Home,
  legal: Scale,
  custom: Building2
};

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  const [step, setStep] = useState(1);
  const [industries, setIndustries] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    industry: searchParams.get("industry") || "",
    email: "",
    password: "",
    phone_number: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const res = await axios.get(`${API}/industries`);
      setIndustries(res.data);
    } catch (error) {
      console.log("Could not fetch industries");
    }
  };

  const handleIndustrySelect = (industryId) => {
    setFormData({ ...formData, industry: industryId });
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password || !formData.industry) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await register(
        formData.name,
        formData.industry,
        formData.email,
        formData.password,
        formData.phone_number
      );
      toast.success("Account created! Welcome to Speakr!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background relative overflow-hidden" data-testid="register-page">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-lg relative bg-card/80 backdrop-blur-sm border-border">
        <CardHeader className="text-center">
          <Link to="/">
            <img src={SPEAKR_LOGO} alt="Speakr" className="h-16 w-auto mx-auto mb-4" />
          </Link>
          <CardTitle className="text-2xl font-heading">
            {step === 1 ? "Choose Your Industry" : "Create Your Account"}
          </CardTitle>
          <CardDescription>
            {step === 1 
              ? "We'll set up AI agents trained for your business type" 
              : "Almost there! Just a few more details"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <div className="grid grid-cols-2 gap-3">
              {industries.map((industry) => {
                const Icon = INDUSTRY_ICONS[industry.id] || Building2;
                return (
                  <button
                    key={industry.id}
                    onClick={() => handleIndustrySelect(industry.id)}
                    className={`p-4 rounded-xl border transition-all text-left hover:border-primary/50 hover:bg-primary/5 ${
                      formData.industry === industry.id 
                        ? "border-primary bg-primary/10" 
                        : "border-border"
                    }`}
                    data-testid={`industry-select-${industry.id}`}
                  >
                    <Icon className="w-6 h-6 mb-2 text-primary" />
                    <p className="font-medium text-sm">{industry.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {industry.agent_count} agent{industry.agent_count !== 1 ? 's' : ''}
                    </p>
                  </button>
                );
              })}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 rounded-lg bg-secondary/50 mb-4">
                <p className="text-xs text-muted-foreground">Selected Industry</p>
                <p className="font-medium">{industries.find(i => i.id === formData.industry)?.name}</p>
                <button 
                  type="button" 
                  onClick={() => setStep(1)}
                  className="text-xs text-primary hover:underline"
                >
                  Change
                </button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  placeholder="Your Business Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="business-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@business.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  data-testid="phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 8 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? "Creating Account..." : "Create Account & Start Free Trial"} 
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          )}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
